import { ReactElement, SyntheticEvent, useMemo, useCallback } from 'react';

import currentTheme from '~config/theme';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';
import { ORDERS_ON_AGENDA_STATE_OPTIONS } from '~constants/ordersStatesOptions';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInTransitFiltersContext } from '../../../InTransitFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';
import { DashboardVariantV2CarrierContextTabId } from '../../../types';
import {
  DashboardVariantV2CarrierStatusInfoWidgetCard,
  DashboardVariantV2CarrierStatusItemsList,
  DashboardVariantV2CarrierHeaderLogoImg,
  // DashboardVariantV2CarrierHeaderCaptionText,
} from '../../SubComponents';

const WIDGET_TAB_ID: DashboardVariantV2CarrierContextTabId = 'inTransit';
const WIDGET_ACCENT_COLOR = currentTheme.palette.primary.main;

const [
  FILE_TYPE_OPTION,
  MANUAL_TYPE_OPTION,
  TN_TYPE_OPTION,
  ML_TYPE_OPTION,
  WOOC_TYPE_OPTION,
  FLEX_TYPE_OPTION,
  SHOPIFY_TYPE_OPTION,
] = SOURCE_IMPORT_TYPES_OPTIONS;

const STANDARD_TYPES_OPTIONS = [
  FILE_TYPE_OPTION,
  MANUAL_TYPE_OPTION,
  TN_TYPE_OPTION,
  ML_TYPE_OPTION,
  WOOC_TYPE_OPTION,
  SHOPIFY_TYPE_OPTION,
];

const [SCHEDULED_STATE_OPTION, IN_PROGRESS_STATE_OPTION] =
  ORDERS_ON_AGENDA_STATE_OPTIONS;

const DashboardVariantV2CarrierInTransitWidgetCard = (): ReactElement => {
  const {
    statistics: {
      data: {
        totalItems,
        inProgress: {
          totalInProgressItems,
          totalInProgressItemsNoFlex,
          totalInProgressItemsFlex,
          totalItemsInProgress,
          totalItemsScheduled,
        },
      },
    },
    activeTab: { getIsActiveTab, setActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const {
    autocompletesRefs: {
      sourceImports: sourceImportsAutocompleteRef,
      itemState: itemStateAutocompleteRef,
    },
    filters: { handleClearFilters, handleClearFilterByValues },
    forceRequest: { toggleValue: toggleForceRequest },
  } = useDashboardVariantV2CarrierInTransitFiltersContext();

  const {
    selectedPreset: { reset: handleResetSelectedPreset },
  } = useDashboardVariantV2CarrierPresetsContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    if (!isSelected) {
      handleResetSelectedPreset();
    }

    setActiveTab(WIDGET_TAB_ID);
  }, [isSelected, setActiveTab, handleResetSelectedPreset]);

  const handleSelectedTabByFilters = useCallback(
    (values: Record<string, unknown>, callback?: () => void) =>
      (event: SyntheticEvent) => {
        event.stopPropagation();

        handleClearFilters(false);
        handleClearFilterByValues(values, () => toggleForceRequest(true));
        handleResetSelectedPreset();

        setActiveTab(WIDGET_TAB_ID);

        if (callback) {
          setTimeout(() => {
            callback();
          }, 150);
        }
      },
    [
      handleClearFilterByValues,
      handleResetSelectedPreset,
      setActiveTab,
      toggleForceRequest,
      handleClearFilters,
    ],
  );

  const handleSelectedTabWithStandardSourceImportTypes = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters(
        { sourceImports: STANDARD_TYPES_OPTIONS },
        () =>
          sourceImportsAutocompleteRef.current?.setValue(
            STANDARD_TYPES_OPTIONS,
          ),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithFlexSourceImportType = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters({ sourceImports: [FLEX_TYPE_OPTION] }, () =>
        sourceImportsAutocompleteRef.current?.setValue([FLEX_TYPE_OPTION]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithScheduledState = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters({ itemState: [SCHEDULED_STATE_OPTION] }, () =>
        itemStateAutocompleteRef.current?.setValue([SCHEDULED_STATE_OPTION]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithInProgressState = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters(
        { itemState: [IN_PROGRESS_STATE_OPTION] },
        () =>
          itemStateAutocompleteRef.current?.setValue([
            IN_PROGRESS_STATE_OPTION,
          ]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  return (
    <DashboardVariantV2CarrierStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      value={{
        current: totalInProgressItems,
        total: totalItems,
      }}
      title={{
        text: 'EN TRÁNSITO',
        tooltip: 'Pedidos que se encuentran en camino a su destino.',
      }}
      icon={{
        src: toAbsoluteUrl('/icons/icon_section_transit.svg'),
        alt: 'Section Transit Icon',
      }}
      content={
        <DashboardVariantV2CarrierStatusItemsList
          items={[
            {
              icon: (
                <DashboardVariantV2CarrierHeaderLogoImg
                  src={toAbsoluteUrl('/logo_minimal.svg')}
                  alt="Routix Icon"
                />
              ),
              label: 'Envíos estándar',
              value: totalInProgressItemsNoFlex,
              onClick: handleSelectedTabWithStandardSourceImportTypes,
            },
            {
              icon: (
                <DashboardVariantV2CarrierHeaderLogoImg
                  src={toAbsoluteUrl('/images/icon_source_mp@2x.png')}
                  alt="MercadoPago Icon"
                />
              ),
              label: 'Envíos Flex',
              value: totalInProgressItemsFlex,
              onClick: handleSelectedTabWithFlexSourceImportType,
            },
            {
              label: 'Pedidos a despachar',
              value: totalItemsScheduled,
              onClick: handleSelectedTabWithScheduledState,
            },
            {
              label: 'Pedidos en viaje',
              value: totalItemsInProgress,
              onClick: handleSelectedTabWithInProgressState,
            },
          ]}
        />
      }
      // footer={
      //   <DashboardVariantV2CarrierHeaderCaptionText color={WIDGET_ACCENT_COLOR}>
      //     Ver rutas en tránsito
      //   </DashboardVariantV2CarrierHeaderCaptionText>
      // }
    />
  );
};

export default DashboardVariantV2CarrierInTransitWidgetCard;
